import { useEffect, useState } from "react";
import { RefreshCw, X } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Aviso de nova versão disponível.
 *
 * Escuta o evento global "sw-update-available" disparado em main.tsx quando o
 * Service Worker baixa uma nova build. Em vez de recarregar a página à força
 * (o que faz o usuário perder formulários em andamento), mostramos um aviso
 * persistente com ação explícita.
 */
export function UpdatePrompt() {
  const [available, setAvailable] = useState(false);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    const handler = () => setAvailable(true);
    window.addEventListener("sw-update-available", handler);
    return () => window.removeEventListener("sw-update-available", handler);
  }, []);

  if (!available) return null;

  const applyUpdate = () => {
    setUpdating(true);
    window.dispatchEvent(new Event("sw-apply-update"));
    // Fallback: se o SW não assumir o controle em 3s, recarrega mesmo assim.
    setTimeout(() => window.location.reload(), 3000);
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-x-0 bottom-0 z-[60] px-3 pb-[calc(env(safe-area-inset-bottom,0px)+0.75rem)] sm:bottom-4 sm:left-auto sm:right-4 sm:px-0 sm:pb-0"
    >
      <div className="mx-auto flex max-w-md items-center gap-3 rounded-lg border border-primary/60 bg-card p-3 shadow-lg">
        <RefreshCw className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground">Nova versão disponível</p>
          <p className="text-xs text-muted-foreground">
            Atualize para receber as últimas correções.
          </p>
        </div>
        <Button size="sm" onClick={applyUpdate} disabled={updating}>
          {updating ? "Atualizando..." : "Atualizar"}
        </Button>
        <button
          type="button"
          onClick={() => setAvailable(false)}
          aria-label="Dispensar aviso de atualização"
          className="text-muted-foreground transition-colors hover:text-foreground"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

export default UpdatePrompt;
